// Env vars are validated here so callers never read `import.meta.env` or the
// runtime config directly. Five service URLs + tenant id + dev-mode flag are
// added per Epic FE-US-INT-01 §4.2.
//
// TWO sources, runtime-first:
//   1. DEPLOYED: `window.__APP_CONFIG__` — injected at container start from the
//      pod's env vars (which the infra repo's dev-deployment.yaml provides) via
//      the generated `/config.js`. This is the source of truth for deployed
//      builds; Vite bakes nothing.
//   2. LOCAL DEV: `import.meta.env` — Vite loads `.env.development` as usual.
// `pick()` takes the runtime value when present, else the Vite build value, so
// local `pnpm dev:us` keeps reading `.env` and the deployed image reads the
// ConfigMap-style env from infra.
//
// `requiredCountry` is required (the app cannot boot without a country code).
// Service URLs have sensible localhost defaults so a developer running
// `pnpm dev:us` doesn't need a .env file checked out.

import type { CountryCode } from '@fluxora/types';

/** Runtime config injected by /config.js (deployed). Empty in local dev. */
const RUNTIME: Record<string, string> =
  (typeof window !== 'undefined' && window.__APP_CONFIG__) || {};

/** Runtime value wins; otherwise fall back to the Vite build-time value. */
function pick(runtimeValue: string | undefined, buildValue: string | undefined): string | undefined {
  if (typeof runtimeValue === 'string' && runtimeValue.length > 0) return runtimeValue;
  return typeof buildValue === 'string' && buildValue.length > 0 ? buildValue : undefined;
}

function required(name: string, value: string | undefined): string {
  if (!value) throw new Error(`Missing required env var: ${name}`);
  return value;
}

function optional(value: string | undefined, fallback: string): string {
  return value && value.length > 0 ? value : fallback;
}

/**
 * Sentinel meaning "this service is reached through a dev-server proxy, so send
 * the request to whatever origin the app is being served from".
 *
 * Written as a word rather than an empty string because empty is already
 * overloaded here: `pick()` treats "" as absent, so an empty var silently falls
 * back to the hardcoded default instead of selecting same-origin.
 */
export const SAME_ORIGIN = 'same-origin';

/**
 * Resolves a service base URL, expanding the {@link SAME_ORIGIN} sentinel to the
 * current page origin.
 *
 * Why not just write `http://localhost:5178` in the .env, which is what the
 * Pricing proxy comment suggests? Two ways that silently misroutes:
 *
 *  1. **Port drift.** `server.strictPort` is `false`, so if 5178 is taken Vite
 *     happily serves on 5179 — which is US-Shop's port. The app would then post
 *     market-control actions at another app's dev server.
 *  2. **Tenant subdomains.** The CRM is served per-tenant on
 *     `<slug>.localhost:5178`. A literal `localhost:5178` is a *different
 *     origin* from `acme.localhost:5178`, so the hardcoded form reintroduces
 *     the very cross-origin request the proxy exists to remove.
 *
 * `window.location.origin` is correct under both. This file is already
 * browser-only (see `authRedirectUri` below), so there is no SSR path to guard.
 */
export function resolveBaseUrl(value: string | undefined, fallback: string): string {
  const raw = optional(value, fallback);
  return raw === SAME_ORIGIN ? window.location.origin : raw;
}

const FALLBACK_TENANT_ID = 'tenant-fixture-001';

export const env = {
  country: required(
    'VITE_COUNTRY',
    pick(RUNTIME['VITE_COUNTRY'], import.meta.env.VITE_COUNTRY),
  ) as CountryCode,

  // Legacy / kept for the existing IE-style createApiClient call sites that
  // still live under US src. Phase 1 will retire this.
  apiBaseUrl: required(
    'VITE_API_BASE_URL',
    pick(RUNTIME['VITE_API_BASE_URL'], import.meta.env.VITE_API_BASE_URL),
  ),

  // Per-service base URLs — see Epic FE-US-INT-01 §4.2.
  pricingApiUrl: optional(pick(RUNTIME['VITE_PRICING_API_URL'], import.meta.env.VITE_PRICING_API_URL), 'http://localhost:3010'),
  quotationApiUrl: optional(pick(RUNTIME['VITE_QUOTATION_API_URL'], import.meta.env.VITE_QUOTATION_API_URL), 'http://localhost:3012'),
  // Bulk-quote (E17) base URL — SEPARATE from `quotationApiUrl` on purpose.
  //
  // The deployed Quotation build does not register `/v1/bulk-quotes` (the
  // composition root never populated the slot), so bulk 404s there while every
  // other quotation route works fine. Splitting the base URL lets bulk point at
  // a locally-run service WITHOUT dragging quotes / contracts / renewals /
  // marketplace onto localhost too — pointing the whole service at a local box
  // would break all of those the moment it is not running.
  //
  // Unset ⇒ falls back to `quotationApiUrl`, so production and CI are unchanged.
  bulkQuoteApiUrl: optional(
    pick(RUNTIME['VITE_BULK_QUOTE_API_URL'], import.meta.env.VITE_BULK_QUOTE_API_URL),
    '',
  ),
  accountsApiUrl: optional(pick(RUNTIME['VITE_ACCOUNTS_API_URL'], import.meta.env.VITE_ACCOUNTS_API_URL), 'http://localhost:3011'),
  // Funnel reads (leads, enquiries) — SEPARATE from `accountsApiUrl` on purpose,
  // the same way `bulkQuoteApiUrl` splits off `quotationApiUrl`: the deployed
  // dev host serves the accounts but has no funnel tables yet, while a local
  // Accounts has the tables and none of the accounts. Unset ⇒ `accountsApiUrl`.
  accountsFunnelApiUrl: optional(
    pick(RUNTIME['VITE_ACCOUNTS_FUNNEL_API_URL'], import.meta.env.VITE_ACCOUNTS_FUNNEL_API_URL),
    '',
  ),
  // Global-Admin (platform/identity control plane, :3020). Backs the pre-login
  // subdomain→tenant resolver (GET /v1/orgs/by-subdomain/:slug) which reads the
  // authoritative country_us.tenant registry incl. per-tenant branding.
  adminApiUrl: optional(pick(RUNTIME['VITE_ADMIN_API_URL'], import.meta.env.VITE_ADMIN_API_URL), 'http://localhost:3020'),
  // Dev/deploy fallback tenant slug for pre-login branding resolution. The org
  // resolver normally derives the slug from the subdomain, but on plain
  // `localhost:5178` (no subdomain) there is none, so branding never loads and
  // the shell shows its generic default. Set VITE_ORG_SLUG to force-resolve a
  // tenant's branding without a subdomain. Empty ⇒ subdomain-only (prod default).
  orgSlug: optional(pick(RUNTIME['VITE_ORG_SLUG'], import.meta.env.VITE_ORG_SLUG), ''),
  // Billing is :3013. The default said :3015 until now, which is GMO's port —
  // a developer with no .env would have pointed the Billing client at the ERCOT
  // market service. `.env.development` already records the 2026-06-04 fix; only
  // this fallback was missed.
  billingApiUrl: optional(pick(RUNTIME['VITE_BILLING_API_URL'], import.meta.env.VITE_BILLING_API_URL), 'http://localhost:3013'),
  notificationApiUrl: optional(pick(RUNTIME['VITE_NOTIFICATION_API_URL'], import.meta.env.VITE_NOTIFICATION_API_URL), 'http://localhost:3014'),
  // MT-US-11 — Metering & Consumption Service (MDM, :3016). Backs the
  // `/ops/metering` console.
  meteringApiUrl: optional(pick(RUNTIME['VITE_METERING_API_URL'], import.meta.env.VITE_METERING_API_URL), 'http://localhost:3016'),
  // Customer Portal BFF (:6060) — the ONLY portal route the CRM calls is the
  // portal-invite pair under `/api/internal/portal-invite*`, for the "Resend
  // portal invite" row action.
  //
  // Cross-origin by nature (the portal is a separate app on a separate host),
  // so the portal must list this CRM's origin in its own `PORTAL_CRM_ORIGINS`
  // or the browser discards the response. The CRM sends its normal staff bearer
  // token; the portal verifies it against the staff realm and checks a role.
  //
  // Accepts the `same-origin` sentinel (see `resolveBaseUrl`) so local dev can
  // reach the DEPLOYED portal through the Vite proxy in vite.config.ts — its
  // CORS allow-list names the deployed CRM origin, not this dev server.
  portalApiUrl: resolveBaseUrl(pick(RUNTIME['VITE_PORTAL_API_URL'], import.meta.env.VITE_PORTAL_API_URL), 'http://localhost:6060'),
  // Broker BFF — brokers live on the Quotation Service (/v1/brokers, :3012) in
  // prod; in dev this points at the Broker Portal BFF (:6070) which reads
  // quotation.broker directly, so the CRM broker module shows real data now.
  brokerApiUrl: optional(pick(RUNTIME['VITE_BROKER_API_URL'], import.meta.env.VITE_BROKER_API_URL), 'http://localhost:6070'),
  // Global-Market-Operations (GMO, :3015) — backs the `/ops/market` console.
  //
  // Defaults to the SAME_ORIGIN sentinel, i.e. through the Vite proxy, because
  // a direct browser call to :3015 CANNOT succeed: GMO sets `origin: false` on
  // @fastify/cors, so the preflight 404s and responses carry no
  // `access-control-allow-origin`. A default of `http://localhost:3015` looks
  // reasonable and fails 100% of the time in a browser, which is worse than no
  // default at all. See the `/api/v1/us/ercot` proxy in `vite.config.ts`.
  //
  // Deployed builds inject an absolute URL via `window.__APP_CONFIG__`, which
  // takes precedence and is unaffected by this default.
  marketOpsApiUrl: resolveBaseUrl(pick(RUNTIME['VITE_MARKET_OPS_API_URL'], import.meta.env.VITE_MARKET_OPS_API_URL), SAME_ORIGIN),

  // DEV-ONLY BRIDGE — opt-in, and it exists because GMO's auth is genuinely
  // broken, not because this CRM's token is wrong.
  //
  // GMO's simulator routes want the `market-ops:admin` role. This CRM issues
  // `portal:crm:*`, and `market-ops:` is a DECOMMISSIONED namespace in
  // @rheora/identity-sdk (`LEGACY_ROLE_PREFIXES`), so there is no correct role
  // to grant on either side until GMO executes AUTH_INTEGRATION_PLAN §4 P1-3.
  // Meanwhile GMO's own dev bypass fires ONLY for a bearer literally starting
  // `dev:` — which neither Keycloak nor DummyAuthProvider ever mints — so every
  // token this app can produce 401s before the role is even considered.
  //
  // With this flag on, the marketOps client sends `dev:<tenantId>:admin`, where
  // the tenant is read from the REAL signed-in session — so it cannot be used to
  // reach another tenant's data, and it is inert unless someone has already
  // authenticated normally. Doubly gated: `import.meta.env.DEV` is statically
  // false in any production build, so this cannot ship.
  //
  // DELETE THIS the day GMO accepts a real token. It is a walkthrough scaffold,
  // not an auth design.
  marketOpsDevAuth:
    import.meta.env.DEV &&
    pick(RUNTIME['VITE_MARKET_OPS_DEV_AUTH'], import.meta.env.VITE_MARKET_OPS_DEV_AUTH) === 'true',

  // The tenant this CRM deployment SERVES. Sent as `X-Tenant-Id`.
  tenantId: optional(
    pick(RUNTIME['VITE_TENANT_ID'], import.meta.env.VITE_TENANT_ID),
    '00000000-0000-4000-8000-000000000001',
  ),

  // Dummy-auth tenant id. Survives until real OIDC lands.
  devTenantId: optional(pick(RUNTIME['VITE_DEV_TENANT_ID'], import.meta.env.VITE_DEV_TENANT_ID), FALLBACK_TENANT_ID),
  devCountryCode: optional(pick(RUNTIME['VITE_DEV_COUNTRY_CODE'], import.meta.env.VITE_DEV_COUNTRY_CODE), 'US'),

  // Hard kill-switch for the dummy auth provider. MUST be 'true' in dev
  // and MUST be 'false' (or unset) in prod builds.
  devMode: pick(RUNTIME['VITE_DEV_MODE'], import.meta.env.VITE_DEV_MODE) === 'true',
  /**
   * LOCAL-BACKEND ESCAPE HATCH (2026-09-16): substitute `dev:<tenant>:admin`
   * bearers on API calls even under the REAL auth provider. For the case where
   * the login is real (Keycloak ROPC via the local Accounts) but the local
   * backends verify only dev tokens (`@rheora/identity-sdk` is a stand-in on
   * dev machines). Applied per target: only a LOCAL base URL gets the dev
   * bearer (see `devBearerAppliesTo` in lib/apiClient), so a deployed
   * VITE_*_API_URL keeps receiving the real token.
   */
  devBearer: pick(RUNTIME['VITE_DEV_BEARER'], import.meta.env.VITE_DEV_BEARER) === 'true',
  /**
   * TEST KNOB — sign out this many seconds after login, without renewing.
   * `0` (the default) means normal behaviour: a ~5-minute token that renews
   * itself indefinitely while the tab is open.
   *
   * For exercising the expiry path by hand without waiting for a real token to
   * lapse. A renewing session never expires, so this session is deliberately
   * stored with no refresh token — see `RopcConfig.testSessionSeconds`.
   *
   * ⚠️ READ ONLY UNDER `import.meta.env.DEV` (see App.tsx). A production build
   * never passes it to the provider, whatever the environment says.
   */
  testSessionSeconds: Number.parseInt(
    pick(RUNTIME['VITE_TEST_SESSION_SECONDS'], import.meta.env.VITE_TEST_SESSION_SECONDS) ?? '0',
    10,
  ),

  // --- Real OIDC provider config (Auth Code + PKCE against Keycloak) --------
  authIssuer: pick(RUNTIME['VITE_AUTH_ISSUER'], import.meta.env.VITE_AUTH_ISSUER) ?? '',
  authClientId: pick(RUNTIME['VITE_AUTH_CLIENT_ID'], import.meta.env.VITE_AUTH_CLIENT_ID) ?? '',
  authRedirectUri: optional(
    pick(RUNTIME['VITE_AUTH_REDIRECT_URI'], import.meta.env.VITE_AUTH_REDIRECT_URI),
    `${window.location.origin}/auth/callback`,
  ),
  authPostLogoutRedirectUri: optional(
    pick(RUNTIME['VITE_AUTH_POST_LOGOUT_REDIRECT_URI'], import.meta.env.VITE_AUTH_POST_LOGOUT_REDIRECT_URI),
    window.location.origin,
  ),
  authScopes: optional(pick(RUNTIME['VITE_AUTH_SCOPES'], import.meta.env.VITE_AUTH_SCOPES), 'openid profile email'),

  sentryDsn: pick(RUNTIME['VITE_SENTRY_DSN'], import.meta.env.VITE_SENTRY_DSN) ?? '',
  mode: import.meta.env.MODE,
  isDev: import.meta.env.DEV,

  // E30 / PUCT §25.475 hard gate. Defaults false.
  eflPublishEnabled: pick(RUNTIME['VITE_FEATURE_EFL_PUBLISH'], import.meta.env.VITE_FEATURE_EFL_PUBLISH) === 'true',

  // --- FE-CRM-04 "Rheora AI" assistant -------------------------------------
  //
  // Chat backend base URL. Default is the EMPTY STRING, deliberately — not a
  // plausible-looking URL. An absolute default would be wrong in every
  // environment but one, and a relative default is worse still: the fetch would
  // resolve against the CRM's own origin, the dev server would answer with
  // `index.html` (Vite returns 200 for every unresolved path), and the assistant
  // would fail forever while every config page reported it as "set". Empty means
  // "not configured", which the caller can detect and say so.
  aiChatApiUrl: optional(pick(RUNTIME['VITE_AI_CHAT_API_URL'], import.meta.env.VITE_AI_CHAT_API_URL), ''),

  // The two booleans below have OPPOSITE defaults on purpose — they are not the
  // same kind of switch.
  //
  //   `aiAssistantEnabled` is a FEATURE gate: absence means the feature has not
  //   been turned on for this environment, so it must default FALSE and ship
  //   dark. Anything else would surface an unreviewed surface to operators the
  //   moment a deploy forgot the var.
  //
  //   `aiAssistantHotkeyEnabled` is a KILL SWITCH for one detail of an
  //   already-enabled feature (the global hotkey, which can collide with a
  //   browser or screen-reader binding). Absence must therefore mean ON —
  //   `!== 'false'` — or every environment that enabled the assistant but did
  //   not know about the hotkey var would silently lose the keyboard path,
  //   which is the accessible one.
  //
  // Neither is guarded on `isProduction`. Deployed environments here run
  // NODE_ENV=development, so a production-only guard is inert in dev AND uat and
  // would give false assurance that the feature cannot escape.
  aiAssistantEnabled:
    pick(RUNTIME['VITE_FEATURE_AI_ASSISTANT'], import.meta.env.VITE_FEATURE_AI_ASSISTANT) === 'true',
  aiAssistantHotkeyEnabled:
    pick(RUNTIME['VITE_FEATURE_AI_ASSISTANT_HOTKEY'], import.meta.env.VITE_FEATURE_AI_ASSISTANT_HOTKEY) !==
    'false',

  // FE-US-11 — Ops enrollment-issues page. Optional MarkeTrak deep-link template.
  marketrakIssueUrlTemplate: optional(
    pick(RUNTIME['VITE_MARKETRAK_ISSUE_URL_TEMPLATE'], import.meta.env.VITE_MARKETRAK_ISSUE_URL_TEMPLATE),
    '',
  ),

  // --- Login page (FE-US login UX) -----------------------------------------
  passwordResetUrl: optional(pick(RUNTIME['VITE_PASSWORD_RESET_URL'], import.meta.env.VITE_PASSWORD_RESET_URL), ''),

  // Support / admin contact for access + password-reset requests.
  supportEmail: optional(pick(RUNTIME['VITE_SUPPORT_EMAIL'], import.meta.env.VITE_SUPPORT_EMAIL), 'admin@rheora.dev'),

  // Optional SSO / external-IdP entry point.
  ssoEnabled: pick(RUNTIME['VITE_SSO_ENABLED'], import.meta.env.VITE_SSO_ENABLED) === 'true',
  ssoLabel: optional(pick(RUNTIME['VITE_SSO_LABEL'], import.meta.env.VITE_SSO_LABEL), 'Sign in with SSO'),
  ssoStartUrl: optional(pick(RUNTIME['VITE_SSO_START_URL'], import.meta.env.VITE_SSO_START_URL), ''),
} as const;
